/**
 * OperatorTopbar — 64px white bar with the universal PageHeader (back button +
 * breadcrumbs), search, and primary action.
 *
 * W66.X26: the left segment now renders <PageHeader />, a route-derived
 * back-button + Home › Section › Page breadcrumb that is identical on every
 * authenticated page. The legacy per-page `breadcrumbs` prop is retained for
 * call-site compatibility but is no longer rendered — PageHeader derives the
 * trail from the current route so the strip is consistent everywhere.
 */
import { Link } from "wouter";
import { Search } from "lucide-react";
import { tokens } from "./tokens";
import GalaxyEntryButton from "@/components/galaxy/GalaxyEntryButton";
import PageHeader from "@/components/layout/PageHeader";

export type Crumb = { label: string; href?: string };

export interface OperatorTopbarProps {
  /** Legacy per-page breadcrumbs — retained for compatibility, no longer rendered (W66.X26). */
  breadcrumbs?: Crumb[];
  searchPlaceholder?: string;
  primaryAction?: {
    label: string;
    onClick?: () => void;
    href?: string;
  };
}

export default function OperatorTopbar({
  searchPlaceholder = "Search accounts, contacts...",
  primaryAction,
}: OperatorTopbarProps) {
  return (
    <header
      className="pl-14 lg:!pl-6"
      style={{
        position: "sticky",
        top: 0,
        height: tokens.space.topbarHeight,
        background: "#FFFFFF",
        borderBottom: `1px solid ${tokens.color.border}`,
        display: "flex",
        alignItems: "center",
        paddingRight: 24,
        gap: 16,
        zIndex: 30,
        fontFamily: tokens.font.body,
        overflow: "hidden",
      }}
    >
      {/* W66.X26: universal back button + Home › Section › Page breadcrumb. */}
      <PageHeader />

      <div style={{ flex: 1 }} />

      {/* Search — hidden on small screens to avoid clobbering breadcrumb */}
      <div
        className="hidden md:flex"
        style={{
          position: "relative",
          width: 320,
          maxWidth: "32vw",
          alignItems: "center",
        }}
      >
        <Search
          size={14}
          strokeWidth={2}
          style={{ position: "absolute", left: 10, color: tokens.color.textSubtle, pointerEvents: "none" }}
        />
        <input
          type="search"
          placeholder={searchPlaceholder}
          style={{
            width: "100%",
            height: 34,
            padding: "0 12px 0 30px",
            background: tokens.color.surfaceMute,
            border: `1px solid ${tokens.color.border}`,
            borderRadius: 6,
            fontSize: 13,
            color: tokens.color.textPrimary,
            outline: "none",
            fontFamily: tokens.font.body,
          }}
        />
      </div>

      {/* W49: Galaxy entry button — persistent on all authenticated routes */}
      <GalaxyEntryButton variant="topbar" />

      {/* Primary action */}
      {primaryAction && (
        primaryAction.href ? (
          <Link
            href={primaryAction.href}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              height: 34, padding: "0 14px",
              background: tokens.color.red,
              color: "#FFFFFF",
              borderRadius: 6,
              fontSize: 13, fontWeight: 600,
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            {primaryAction.label}
          </Link>
        ) : (
          <button
            type="button"
            onClick={primaryAction.onClick}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              height: 34, padding: "0 14px",
              background: tokens.color.red,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 6,
              fontSize: 13, fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {primaryAction.label}
          </button>
        )
      )}
      {/* Red accent strip — 3px bottom bar */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "#DC2626",
        }}
      />
    </header>
  );
}
